import React, { createRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';

import ToolBarAction from './toolbar-action';
import { setShowFolderList } from '../../actions/folders';
import { setShowPreview } from '../../actions/application';
import HideIcon from '../../assets/icons/hide.svg?react';
import ShowIcon from '../../assets/icons/show.svg?react';
import SyncButton from './sync-button';
import MessagesFilter from './messages-filter';
import './list-toolbar.scss';

const TopBarMessageViewer = (
  {
    clearSelectedMessage,
    onReplyMessageClick,
    onReplyAllMessageClick,
    onForwardMessageClick,
    onDeleteClick,
    onPrint,
    onMarkReadClick,
    onRefreshClick,
    updatedShowPreview,
    showPreview,
    isFullMessageContent,
    selectedMessagesCounter,
    setShowFolderList,
  }) => {
  const [displayExpandedToolbar, setDisplayExpandedToolbar] = useState(true);
  const MIN_TOOLBAR_WIDTH_PX = 1200;
  const toolbarRef = createRef();
  const { t } = useTranslation();

  useEffect(() => {
    const toolbarElement = toolbarRef.current;
    const resizeObserver = new ResizeObserver(entries => {
      for (const entry of entries) {
        if (entry.contentRect.width < MIN_TOOLBAR_WIDTH_PX) {
          setDisplayExpandedToolbar(false);
        } else {
          setDisplayExpandedToolbar(true);
        }
      }
    });

    if (toolbarElement) {
      resizeObserver.observe(toolbarElement);
    }

    return () => {
      if (toolbarElement) {
        resizeObserver.unobserve(toolbarElement);
      }
    };
  }, []);

  return (
    <div className={`msg-toolbar`} ref={toolbarRef}>
      <div className="pl-2 py-2 pr-4 d-flex align-items-center bd-highlight">
        <div className="d-flex align-content-center align-items-center">
          {isFullMessageContent && (
            <div className="px-2">
              <ToolBarAction
                onClick={clearSelectedMessage}
                icon="lf-icon-angle-left"
                title={t('message-toolbar.back')}
                hideText={!displayExpandedToolbar}
                disabled={selectedMessagesCounter >= 2 || (showPreview && isFullMessageContent)}
              />
            </div>)}
          <div className="px-2">
            <ToolBarAction
              onClick={onReplyMessageClick}
              icon="lf-icon-reply"
              title={t('message-toolbar.reply')}
              hideText={!displayExpandedToolbar}
              disabled={selectedMessagesCounter >= 2 || (showPreview && isFullMessageContent)}
            />
          </div>
          <div className="px-2">
            <ToolBarAction
              onClick={onReplyAllMessageClick}
              icon="lf-icon-reply-all"
              title={t('message-toolbar.reply-all')}
              hideText={!displayExpandedToolbar}
              disabled={selectedMessagesCounter >= 2 || (showPreview && isFullMessageContent)}
            />
          </div>
          <div className="px-2">
            <ToolBarAction
              onClick={onForwardMessageClick}
              icon="lf-icon-send"
              title={t('message-toolbar.resend')}
              hideText={!displayExpandedToolbar}
              disabled={selectedMessagesCounter >= 2 || (showPreview && isFullMessageContent)}
            />
          </div>
          <div className="px-2">
            <ToolBarAction
              onClick={onMarkReadClick}
              icon="lf-icon-mail-open"
              title={t('message-toolbar.read-unread')}
              hideText={!displayExpandedToolbar}
            />
          </div>
          <div className="">
            <ToolBarAction
              id="move-to-folder-popover"
              onClick={() => setShowFolderList(true)}
              icon="lf-icon-folder"
              title={t("message-toolbar.move-to")}
              hideText={!displayExpandedToolbar}
            />
          </div>
          <div className="px-2">
            <ToolBarAction
              onClick={onPrint}
              icon="lf-icon-print"
              title={t('message-toolbar.print')}
              hideText={!displayExpandedToolbar}
              disabled={selectedMessagesCounter >= 2 || !showPreview && !isFullMessageContent}
            />
          </div>
          <div className="px-2">
            <ToolBarAction
              onClick={onDeleteClick}
              icon="lf-icon-trash"
              title={t('message-toolbar.move-to-trash')}
              hideText={!displayExpandedToolbar}
            />
          </div>
        </div>
        {!isFullMessageContent &&
          <div className={`right-buttons`}>
            <SyncButton onSyncClick={onRefreshClick} />
            <MessagesFilter/>
            <div className="toolbar-divider"/>
            <div
              className={`
                    d-flex
                    align-items-center
                    toolbar-preview-container
                    ${!showPreview ? 'w-preview' : 'w-hide-preview'}`
              }
              onClick={() => {
                updatedShowPreview(!showPreview);
              }}
            >
              {!showPreview ? <ShowIcon /> : <HideIcon />}
              <span
                className="ml-2 text-color-blue-lefebvre inter-semibold-14">
                {!showPreview ? t('topBar.preview') : t('topBar.hide_preview')}
              </span>
            </div>
          </div>}
      </div>
    </div>
  );
};

TopBarMessageViewer.propTypes = {
  clearSelectedMessage: PropTypes.func.isRequired,
  outboxEmpty: PropTypes.bool.isRequired,
  onReplyMessageClick: PropTypes.func.isRequired,
  onForwardMessageClick: PropTypes.func.isRequired,
  onDeleteClick: PropTypes.func.isRequired,
  onPrint: PropTypes.func.isRequired,
  onMarkReadClick: PropTypes.func.isRequired
};

const mapStateToProps = state => ({
  showPreview: state.application.showPreview,
  isFullMessageContent: state.messages.isFullMessageContent
});

const mapDispatchToProps = dispatch => ({
  updatedShowPreview: showPreview => dispatch(setShowPreview(showPreview)),
  setShowFolderList: showFolderList => dispatch(setShowFolderList(showFolderList)),
});

const ConnectedTopBarMessageViewer = connect(
  mapStateToProps,
  mapDispatchToProps
)(TopBarMessageViewer);

export default ConnectedTopBarMessageViewer;
